import { useEffect, useRef } from "react";

/* The stamp grid: the thing that makes a card feel like a card.
 *
 * A filled pip carries `--pip-filled`, which in the plum register is a
 * gradient from ochre to seal red, read off the artboard rather than guessed.
 * It is the one gradient in the product that means something: the accent and
 * the brand's red, in the mark a customer has actually earned. An empty pip is
 * a hairline, so the grid reads as a row of places rather than a row of holes.
 *
 * Pips are the one genuinely circular thing in a 2px-corner system, so they
 * take `--radius-full` without breaking the shape rule.
 *
 * NO COUNT-UP ANIMATION. A customer opens this screen many times a day, and
 * an animation on something seen that often stops reading as delight and
 * starts reading as latency. The pips are simply drawn in their current state.
 */
/**
 * How many pips go on a row.
 *
 * Flex-wrap fills each row to the container and drops the remainder, so a
 * twelve-stamp card came out as ten and then two, which reads as a mistake
 * rather than as a grid. Rows are balanced instead: the fewest rows that fit
 * within `maxPerRow`, then divided evenly across them. Twelve becomes six and
 * six; eight stays on one line; twenty becomes ten and ten.
 */
export function pipColumns(total: number, maxPerRow = 10): number {
  if (total <= maxPerRow) return total;
  const rows = Math.ceil(total / maxPerRow);
  return Math.ceil(total / rows);
}

export function StampGrid({
  filled,
  total,
  size = 22,
  maxPerRow = 10,
}: {
  filled: number;
  total: number;
  size?: number;
  maxPerRow?: number;
}) {
  // Defensive rather than trusting: a balance past the threshold is a real
  // state (the reward is issued but not yet claimed) and must not render as a
  // grid with more pips than it has slots.
  const shown = Math.max(0, Math.min(filled, total));

  /* WHICH PIPS JUST LANDED.
   *
   * The wallet polls now, so a stamp can arrive while the customer is looking
   * at the card. A count that silently increments is not a moment -- and that
   * moment is the product: it is the only point where the customer feels
   * anything at all. So the pips that were not filled a render ago carry
   * `data-landing` for one animation, and the rest do not move.
   *
   * A ref rather than state: this must not cause a render of its own, or the
   * animation retriggers on every parent update and the card twitches.
   */
  const previous = useRef(shown);
  const landedFrom = previous.current < shown ? previous.current : shown;
  useEffect(() => {
    previous.current = shown;
  }, [shown]);

  return (
    <div
      className="dg-pips"
      style={{
        ["--pip" as string]: `${size}px`,
        ["--pip-cols" as string]: String(pipColumns(total, maxPerRow)),
      }}
      role="img"
      aria-label={`${filled} of ${total} stamps`}
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          data-filled={i < shown ? "" : undefined}
          data-landing={i >= landedFrom && i < shown ? "" : undefined}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export const stampGridCss = `
.dg-pips {
  display: grid;
  grid-template-columns: repeat(var(--pip-cols, 8), var(--pip));
  justify-content: start;
  gap: 8px;
}
.dg-pips > span {
  width: var(--pip);
  height: var(--pip);
  border-radius: var(--radius-full);
  border: 1px solid var(--pip-empty-border, var(--border));
  background: transparent;
}
@keyframes dg-pip-land {
  0%   { transform: scale(0.4); opacity: 0.35; }
  55%  { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
/* The stamp landing. Short, once, and only on the pip that changed.
   prefers-reduced-motion turns it off rather than shortening it: the pip still
   fills, it just does not move. */
.dg-pips > span[data-landing] {
  animation: dg-pip-land 420ms cubic-bezier(0.22, 0.9, 0.28, 1) both;
}
@media (prefers-reduced-motion: reduce) {
  .dg-pips > span[data-landing] { animation: none; }
}
.dg-pips > span[data-filled] {
  background: var(--pip-filled, var(--seal));
  border-color: transparent;
}
`;
